import React from "react";
import { Typography, Divider, Button } from "antd";
import { Plan } from "../../../../Entities/Plan";
import { openNotification } from "../../../../Components/Notification";
import { useLoader } from "../../../../Context/LoaderContext";
import creditService from "../../../../Service/credit.service";
import useRazorpay from "react-razorpay";
import { appName, appLogo } from "../../../../Utils/assets.constants";
import useUser from "../../../../Hooks/useUser";
import useOrganization from "../../../../Hooks/useOrganization";
import { useNavigate } from "react-router-dom";
import useAccess from "../../../../Hooks/useAccess";
import { extractBasePrice } from "../../../../Utils/planUtils";
import "./CreditSummary.scss";
import SubscriptionInfo from "./SubscriptionInfo";

interface CreditSummaryProps {
  noOfSelectedCredits: number | null;
  userCurrencyCode: "INR" | "USD";
  userCurrencySymbol: "₹" | "$";
  creditPlan: Plan;
}

const CreditSummary: React.FC<CreditSummaryProps> = ({
  noOfSelectedCredits,
  userCurrencyCode,
  userCurrencySymbol,
  creditPlan,
}) => {
  const { setLoading, setLoadingMessage } = useLoader();
  const [Razorpay] = useRazorpay();
  const { user } = useUser();
  const { org } = useOrganization();
  const navigate = useNavigate();
  const { currentSubscription } = useAccess();

  const selectedVariant = creditPlan.variants.find(
    (variant) =>
      variant.currency.toLowerCase() === userCurrencyCode.toLowerCase() &&
      variant.billingPeriod.toLowerCase() === "month",
  );

  const inclusivePricePerCredit = selectedVariant?.price || 0;
  const basePricePerCredit = extractBasePrice(inclusivePricePerCredit, userCurrencyCode);
  const credits = noOfSelectedCredits || 0;
  const isINR = userCurrencyCode === "INR";
  const subtotal = basePricePerCredit * credits;
  const total = inclusivePricePerCredit * credits;
  const gst = total - subtotal;

  const formatCurrency = (amount: number): string => {
    return amount.toFixed(2);
  };

  const handlePaymentSuccess = () => {
    openNotification("success", "Payment Successful", "");
    setLoading(false);
    setLoadingMessage(null);
    navigate("/accounts");
  };

  const handlePaymentFailure = () => {
    openNotification("error", "Payment Failed", "Please try again");
    setLoading(false);
    setLoadingMessage(null);
  };

  const handleRazorpayOrderPayment = async (
    orderId: string,
    creditPlanId: string,
  ) => {
    const razorpayKey = process.env.REACT_APP_RZP_KEY || null;
    if (!razorpayKey) {
      openNotification("error", "Razorpay Key Not Found", "Please try again");
      setLoading(false);
      setLoadingMessage(null);
      return;
    }

    if (!user) {
      openNotification("error", "User Not Found", "Please try again");
      setLoading(false);
      setLoadingMessage(null);
      return;
    }

    if (!org) {
      openNotification("error", "Organization Not Found", "Please try again");
      setLoading(false);
      setLoadingMessage(null);
      return;
    }

    try {
      const options: any = {
        key: razorpayKey,
        order_id: orderId,
        name: appName,
        image: appLogo,
        description: "QualifyMe Job Application Credit purchase payment.",
        prefill: {
          name: user.firstName + " " + user.lastName,
          email: user.email,
        },
        theme: {
          hide_topbar: true,
          color: org.theme.primaryColor,
        },
        modal: {
          backdropclose: false,
          escape: false,
          confirm_close: true,
          ondismiss: () => {
            openNotification("warning", "Payment Cancelled", "");
            setLoading(false);
            setLoadingMessage(null);
          },
        },
        handler: async function (response: any) {
          setLoading(true);
          setLoadingMessage("Verifying payment...");

          const success = await creditService.verifyCreditPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            creditPlanId: creditPlanId,
          });

          setLoading(false);
          setLoadingMessage(null);

          if (success) {
            handlePaymentSuccess();
          } else {
            handlePaymentFailure();
          }
        },
      };

      const rzpay = new Razorpay(options);
      rzpay.on("payment.failed", function (response: any) {
        console.error("Payment failed: ", response);
        handlePaymentFailure();
      });
      rzpay.open();
    } catch (error: any) {
      console.error("Error in handleRazorpayOrderPayment: ", error);
      openNotification("error", "Error in Payment", "Please try again");
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handlePayNow = async () => {
    if (credits <= 0) {
      openNotification("error", "Quantity Not Found", "Please try again");
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage("Initiating credit purchase...");
      const response = await creditService.buyCredits(
        credits,
        userCurrencyCode,
      );
      if (response && response.order && response.creditPlanId) {
        setLoading(false);
        setLoadingMessage(null);
        // Initiate Razorpay payment with the order ID
        await handleRazorpayOrderPayment(
          response.order.id,
          response.creditPlanId,
        );
      } else {
        openNotification("error", "Error Creating Order", "Please try again");
        setLoading(false);
        setLoadingMessage(null);
      }
    } catch (error: any) {
      console.error("Error buying credits", error);
      openNotification("error", "Error Buying Credits", "Please try again");
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  return (
    <div className="credit-summary">
      <Typography.Title level={5} style={{ margin: 0 }}>
        Summary
      </Typography.Title>

      <Divider style={{ margin: "0" }} />

      {currentSubscription && currentSubscription.mode === "base" && (
        <SubscriptionInfo
          subscription={currentSubscription}
          selectedPlanId={currentSubscription.planId}
        />
      )}

      <div className="credit-summary__item">
        <Typography.Text>Qty</Typography.Text>
        <Typography.Text>{credits}</Typography.Text>
      </div>

      <div className="credit-summary__item">
        <Typography.Text>Price per Credit</Typography.Text>
        <Typography.Text>
          {userCurrencySymbol}
          {formatCurrency(basePricePerCredit)}
        </Typography.Text>
      </div>

      <Divider style={{ margin: "0" }} />

      <div className="credit-summary__item">
        <Typography.Text>Subtotal</Typography.Text>
        <Typography.Text>
          {userCurrencySymbol}
          {formatCurrency(subtotal)}
        </Typography.Text>
      </div>

      {isINR ? (
        <div className="credit-summary__item">
          <Typography.Text>Tax (GST 18%)</Typography.Text>
          <Typography.Text>
            {userCurrencySymbol}
            {formatCurrency(gst)}
          </Typography.Text>
        </div>
      ) : (
        <div className="credit-summary__item">
          <Typography.Paragraph style={{ margin: 0 }}>
            <strong>Tax: {userCurrencySymbol}0.00</strong> — No tax has been
            charged as the customer is located outside India and international
            transactions are not subject to Indian tax.
          </Typography.Paragraph>
        </div>
      )}

      <div className="credit-summary__item">
        <Typography.Text strong>
          Total Payable {isINR && "(Inc. tax)"}
        </Typography.Text>
        <Typography.Text strong>
          {userCurrencySymbol}
          {formatCurrency(total)}
        </Typography.Text>
      </div>

      <Divider style={{ margin: "0" }} />

      <Button
        type="primary"
        block
        onClick={handlePayNow}
        disabled={credits === 0 || credits < 1}
        className="credit-summary__pay-button"
      >
        Proceed to Payment
      </Button>
    </div>
  );
};

export default CreditSummary;
